
class CollapsibleRows {
  constructor() {
    this.initializeAllRows();
    this.setupEventListeners();
  }

  initializeAllRows() {
    // Find all collapsible row containers
    document.querySelectorAll('[data-collapsible-block-id]').forEach(container => {
      this.initializeBlock(container);
    });
  }

  initializeBlock(container) {
    const blockId = container.dataset.collapsibleBlockId;
    const allowMultiple = container.dataset.allowMultiple === 'true';
    const firstRowOpen = container.dataset.firstRowOpen === 'true';
    
    // Get all rows in this block
    const rows = container.querySelectorAll('.collapsible-row-item-' + blockId);
    
    rows.forEach((row, index) => {
      // Mark as initialized to prevent duplicate listeners
      if (row.dataset.initialized === 'true') return;
      
      const header = row.querySelector('.collapsible-row-header-' + blockId);
      const content = row.querySelector('.collapsible-row-content-' + blockId);
      
      if (!header || !content) return;
      
      // Open first row if enabled
      if (index === 0 && firstRowOpen) {
        row.classList.add('active');
        header.setAttribute('aria-expanded', 'true');
        
        // Set explicit max-height for smooth transitions
        requestAnimationFrame(() => {
          content.style.maxHeight = content.scrollHeight + 'px';
        });
      }
      
      // Add click handler
      header.addEventListener('click', () => this.toggleRow(row, container));
      
      // Add keyboard support
      header.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleRow(row, container);
        }
      });
      
      // Mark as initialized
      row.dataset.initialized = 'true';
    });
  }

  toggleRow(row, container) {
    const blockId = container.dataset.collapsibleBlockId;
    const allowMultiple = container.dataset.allowMultiple === 'true';
    const header = row.querySelector('.collapsible-row-header-' + blockId);
    const content = row.querySelector('.collapsible-row-content-' + blockId);
    
    if (!header || !content) return;
    
    const isActive = row.classList.contains('active');
    
    // Close all other rows if multiple not allowed
    if (!allowMultiple && !isActive) {
      const allRows = container.querySelectorAll('.collapsible-row-item-' + blockId);
      allRows.forEach(otherRow => {
        if (otherRow !== row && otherRow.classList.contains('active')) {
          this.closeRow(otherRow, container);
        }
      });
    }
    
    // Toggle current row
    if (isActive) {
      this.closeRow(row, container);
    } else {
      this.openRow(row, container);
    }
  }

  openRow(row, container) {
    const blockId = container.dataset.collapsibleBlockId;
    const header = row.querySelector('.collapsible-row-header-' + blockId);
    const content = row.querySelector('.collapsible-row-content-' + blockId);
    
    row.classList.add('active');
    header.setAttribute('aria-expanded', 'true');
    
    // Set max-height for animation
    content.style.maxHeight = content.scrollHeight + 'px';
  }

  closeRow(row, container) {
    const blockId = container.dataset.collapsibleBlockId;
    const header = row.querySelector('.collapsible-row-header-' + blockId);
    const content = row.querySelector('.collapsible-row-content-' + blockId);
    
    row.classList.remove('active');
    header.setAttribute('aria-expanded', 'false');
    content.style.maxHeight = null;
  }

  setupEventListeners() {
    // Listen for dynamically added content (if using AJAX/turbo)
    if (typeof Turbo !== 'undefined') {
      document.addEventListener('turbo:load', () => this.initializeAllRows());
    }
    
    // MutationObserver for dynamically added content
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if node is a collapsible container
            if (node.matches && node.matches('[data-collapsible-block-id]')) {
              this.initializeBlock(node);
            }
            // Check if node contains collapsible containers
            const containers = node.querySelectorAll
              ? node.querySelectorAll('[data-collapsible-block-id]')
              : [];
            containers.forEach(container => this.initializeBlock(container));
          }
        });
      });
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.collapsibleRows = new CollapsibleRows();
});

// Export for use in theme.js if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CollapsibleRows;
}